import { Flex, DialogV2, ButtonV2, TextV2, CancelButton } from '@withjoy/joykit';
import React, { useState } from 'react';
import { useYourRegistryTranslations } from '@apps/registry/admin/routes/YourRegistry/YourRegistry.i18n';
import CashFundEmailPreview from '@assets/email-preview.png';
import { pxToRem } from '@withjoy/joykit/theme';
import { ReminderSentConfirmationDialog } from './ReminderSentConfirmationDialog';
import { useEventInfo } from '@shared/utils/eventInfo';

export interface CashFundPreviewSendEmailReminderDialogProps
  extends Readonly<{
    isOpen: boolean;
    handleOnCancelClick: () => void;
    handleOnSendClick: () => void;
    guestName?: string;
    paymentMethod?: string;
  }> {}

export const CashFundPreviewSendEmailReminderDialog: React.FC<CashFundPreviewSendEmailReminderDialogProps> = ({
  handleOnCancelClick,
  handleOnSendClick,
  isOpen,
  guestName = '',
  paymentMethod = '',
}) => {
  const { getCashFundPreviewSendEmailReminderDialogTranslations } = useYourRegistryTranslations();
  const { header, body, sampleText, cancelButton, confirmButton, emailPreview, from, to, subject, subjectText, joy, toText } =
    getCashFundPreviewSendEmailReminderDialogTranslations();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { eventInfo } = useEventInfo();

  const eventDisplayName = eventInfo?.eventDisplayName || '';

  const handleSendReminder = () => {
    setShowConfirmation(true);
    handleOnSendClick();
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
    handleOnCancelClick();
  };

  return (
    <>
      <DialogV2
        isOpen={isOpen}
        disableAutoFocus={true}
        disableCloseOnOutsideClick={true}
        disableCloseOnEscapeKeyClick={true}
        size="lg"
      >
        <DialogV2.Header
          typographyVariant="hed2"
          paddingBottom={3}
          display="flex"
          justifyContent="center"
        >
          {header}
        </DialogV2.Header>
        <DialogV2.CloseButton onClick={handleOnCancelClick} />
        <DialogV2.Body>
          <Flex
            flexDirection="column"
            alignItems="start"
          >
            <TextV2
              marginBottom={5}
              typographyVariant="body1"
              color="mono12"
              display="flex"
              justifyContent="center"
              width="100%"
            >
              {body({ guestName })}
            </TextV2>
            <TextV2
              display="flex"
              justifyContent="center"
              typographyVariant="hed1"
              color="mono12"
              marginBottom={5}
              width="100%"
            >
              {emailPreview}
            </TextV2>
            <Flex
              color="mono14"
              marginBottom={1}
            >
              <TextV2
                fontSize={pxToRem(12)}
                fontWeight={500}
                marginRight={1}
              >
                {from}
              </TextV2>

              <TextV2
                fontSize={pxToRem(12)}
                fontWeight={400}
              >
                {joy}
              </TextV2>
            </Flex>
            <Flex
              color="mono14"
              marginBottom={1}
            >
              <TextV2
                fontSize={pxToRem(12)}
                fontWeight={500}
                marginRight={1}
              >
                {to}
              </TextV2>

              <TextV2
                fontSize={pxToRem(12)}
                fontWeight={400}
              >
                {toText({ guestName })}
              </TextV2>
            </Flex>
            <Flex
              color="mono14"
              marginBottom={5}
            >
              <TextV2
                fontSize={pxToRem(12)}
                fontWeight={500}
                marginRight={1}
              >
                {subject}
              </TextV2>

              <TextV2
                fontSize={pxToRem(12)}
                fontWeight={400}
              >
                {subjectText({ eventName: eventDisplayName })}
              </TextV2>
            </Flex>
            <Flex
              flexDirection="column"
              alignItems="center"
              width="100%"
            >
              <img
                src={CashFundEmailPreview}
                alt={sampleText}
                width={273}
              />
            </Flex>
          </Flex>
        </DialogV2.Body>
        <DialogV2.Footer
          flexDirection={'column'}
          alignItems={['initial', null, 'center']}
        >
          <ButtonV2
            width="100%"
            shape="rounded"
            intent="neutral"
            marginBottom={5}
            onClick={handleSendReminder}
          >
            {confirmButton}
          </ButtonV2>
          <CancelButton onClick={handleOnCancelClick}>{cancelButton}</CancelButton>
        </DialogV2.Footer>
      </DialogV2>

      <ReminderSentConfirmationDialog
        isOpen={showConfirmation}
        onClose={handleCloseConfirmation}
        guestName={guestName}
        paymentMethod={paymentMethod}
      />
    </>
  );
};
